import { type Href, Link, router } from 'expo-router';
import { useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    H2Regular,
    XStack,
    YStack,
} from 'smoothie';

import { billReviewSourceAtom } from '../../../bills/state/BillsAtoms';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    type SpinwheelConnectRequest,
    useSpinwheelConnect,
} from '../../hooks/useSpinwheelConnect';

const SpinwheelLanding = () => {
    const { trackViewOnce } = useViewEvents();
    const user = useAuthedUser();
    const { mutate: connectToSpinwheel, isPending: isConnecting } =
        useSpinwheelConnect();
    const { source } = useLocalSearchParams<{ source?: string }>();
    const billReviewSource = useAtomValue(billReviewSourceAtom);

    trackViewOnce(`Onboarding/SpinwheelLanding`);

    const handleConnectToSpinwheel = () => {
        const {
            dateOfBirth: userDob,
            phoneNumber: userPhone,
            id: userId,
        } = user;

        if (user.spinwheelId) {
            // Preserve source param when navigating to bills-processing
            const sourceParam =
                source ??
                (billReviewSource === 'home-review'
                    ? 'before-money-map-session'
                    : billReviewSource === 'home'
                      ? 'home'
                      : undefined);
            router.navigate({
                pathname: '/onboarding/expenses/bills-processing',
                ...(sourceParam != null &&
                    sourceParam !== '' && { params: { source: sourceParam } }),
            });
        } else {
            if (userDob == null || userPhone == null || !userId) {
                router.navigate({
                    pathname: '/onboarding/expenses/error',
                    params: {
                        title: 'Missing information',
                        description:
                            'We are unable to connect your debts due to missing some information. Please try again.',
                        primaryButtonText: 'Try again',
                        primaryButtonRoute: '/onboarding/expenses/disclaimer',
                    },
                });
                return;
            }

            // Format date of birth to YYYY-MM-DD (remove time part)
            const dateOfBirth = new Date(userDob)
                .toISOString()
                .split('T')[0] as string;
            const extUserId = userId;
            const phoneNumber = userPhone;

            const requestBody: SpinwheelConnectRequest = {
                dateOfBirth,
                extUserId,
                phoneNumber,
            };

            connectToSpinwheel(
                {
                    body: requestBody,
                },
                {
                    onSuccess: (data) => {
                        // Navigate to verification page with spinwheelUserId
                        router.navigate({
                            pathname: '/onboarding/expenses/verification',
                            params: {
                                spinwheelUserId: data.spinwheelUserId,
                            },
                        });
                    },
                    onError: (error: Error) => {
                        // Check for invalid phone number error (multiple ways it might come)
                        const errorMessage = error.message || '';
                        const errorCode = (
                            error as Error & { json?: { errorCode?: string } }
                        )?.json?.errorCode;

                        // Check for phone format error (multiple possible formats)
                        if (
                            errorCode === 'INVALID_PHONE_FORMAT' ||
                            errorCode === 'UNSUPPORTED_NUMBER' ||
                            errorMessage.includes('phone number is invalid') ||
                            errorMessage.includes('invalid phone') ||
                            errorMessage.includes('phone format')
                        ) {
                            // TODO: build out better logic for determining messages to display on next screen
                            const message =
                                errorCode === 'UNSUPPORTED_NUMBER'
                                    ? "Your phone number can't receive verification texts. Please enter a different mobile number."
                                    : null;
                            const route = `/onboarding/expenses/phone-verification${message ? `?message=${message}` : ''}`;
                            router.navigate(route as Href);
                        } else {
                            // Navigate to generic error page for other errors
                            router.navigate({
                                pathname: '/onboarding/expenses/error',
                                params: {
                                    title: 'Something went wrong',
                                    description:
                                        'We’re here to help! Please reach out to our hospitality team at support@fruitful.com.',
                                    primaryButtonText:
                                        'Contact Fruitful support',
                                },
                            });
                        }
                    },
                },
            );
        }
    };

    const handleGoBack = () => {
        router.back();
    };

    return (
        <FullScreenContainer
            header={
                <XStack
                    padding={16}
                    flex={1}
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    <BackButton
                        fallbackRoute="/home"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                </XStack>
            }
        >
            <YStack
                flex={1}
                paddingHorizontal={24}
                paddingTop={16}
                paddingBottom={32}
            >
                {/* Main Content */}
                <YStack gap={32}>
                    {/* Heading */}
                    <H2Regular color="$black">
                        Now, let&apos;s look for any debt payments
                    </H2Regular>

                    {/* How it works section */}
                    <YStack
                        backgroundColor="$gray100"
                        padding={16}
                        borderRadius={8}
                        gap={12}
                    >
                        <XStack alignItems="center" gap={8}>
                            <BodyMediumRegular fontWeight="600" color="$black">
                                How it works
                            </BodyMediumRegular>
                        </XStack>

                        <YStack gap={8}>
                            <BodyMediumRegular color="$gray1000">
                                We work with a company called Spinwheel to look
                                for credit card and loan payments, rather than
                                you providing these details manually.
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray1000">
                                We use your birthday and phone number to
                                securely request this information from consumer
                                reporting agencies.
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray1000">
                                This will not impact your credit score.
                            </BodyMediumRegular>
                        </YStack>
                    </YStack>

                    {/* Legal text */}
                    <BodyMediumRegular
                        color="$gray1000"
                        fontSize={14}
                        lineHeight={20}
                    >
                        By clicking &quot;Continue&quot; you agree to the{' '}
                        <Link
                            href="https://legal.spinwheel.io/end-user-agreement/"
                            target="_blank"
                        >
                            <BodyMediumRegular
                                textDecorationLine="underline"
                                color="$gray1000"
                                fontSize={14}
                                lineHeight={20}
                            >
                                Spinwheel End User Agreement
                            </BodyMediumRegular>
                        </Link>
                        . Further, you are providing &quot;written
                        instructions&quot; to Spinwheel Solutions, Inc.
                        authorizing it to obtain your credit profile from any
                        consumer reporting agency.
                    </BodyMediumRegular>
                </YStack>

                {/* Bottom section with button - pushed to bottom */}
                <YStack
                    gap={12}
                    alignItems="center"
                    marginTop="auto"
                    paddingTop={24}
                >
                    <Button
                        text={isConnecting ? 'Connecting...' : 'Continue'}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        width="full"
                        onPress={handleConnectToSpinwheel}
                        disabled={isConnecting}
                    />
                </YStack>
            </YStack>
        </FullScreenContainer>
    );
};

export default SpinwheelLanding;
